import { Dayjs } from "dayjs";
import HttpClient from "../../../../core/HttpClient";
import { Authentication } from "@leedpulse-monorepo/classes";
import type { VoipMissedReason } from "@leedpulse-monorepo/interfaces";

// Mirrors `MissedReason` in backend/src/helpers/livekit/voip-pipeline/voipcall-doc.ts
export type MissedReason = VoipMissedReason;

export interface VoipCallRow {
    /** Firestore document id (`doc.id`) — the LEED-1135 derived id for a call
     *  created after the migration, or the raw room name for a pre-migration
     *  doc. Distinct from `roomName`: recording lookup (`GET /calls/:id/recording`)
     *  resolves by DOCUMENT id, not by room name. */
    id: string;
    roomName: string;
    created: number | null;
    direction: string | null;
    status: string | null;
    party: string | null;
    did: string | null;
    ownerEmail: string | null;
    contactName: string | null;
    durationSeconds: number | null;
    missed: boolean | null;
    missedReason: MissedReason | null;
    failuresCount: number;
    /** Reference sequence the call was graded against. */
    scenario: string;
    scenarioLabel: string;
    /** Departures from that sequence. Distinct from `failuresCount`: a wrong
     *  event ORDER is never a stamped error, so it counts 0 failures. */
    anomaliesCount: number;
}

export interface VoipCallFilters {
    search?: string;
    status?: string;
    direction?: string;
    missedReason?: MissedReason;
    onlyFailed?: boolean;
    onlyAnomalies?: boolean;
    limit?: number;
}

/**
 * Lists recent LiveKit calls for the backoffice VoIP debug view (uses the
 * Admin SDK server-side to read the nested `voip` map and bypass Firestore
 * security rules).
 */
async function listVoipCalls(
    auth: Authentication | null,
    period?: [Dayjs | null, Dayjs | null],
    filters?: VoipCallFilters,
): Promise<VoipCallRow[]> {
    if (!auth) return [];

    const body: {
        period?: { from: number; to: number };
    } & VoipCallFilters = {};

    if (period && period[0] && period[1]) {
        body.period = {
            from: period[0].startOf('day').toDate().getTime(),
            to: period[1].endOf('day').toDate().getTime(),
        };
    }
    if (filters?.search) body.search = filters.search;
    if (filters?.status) body.status = filters.status;
    if (filters?.direction) body.direction = filters.direction;
    if (filters?.missedReason) body.missedReason = filters.missedReason;
    if (filters?.onlyFailed) body.onlyFailed = true;
    if (filters?.limit) body.limit = filters.limit;

    try {
        const response = await HttpClient.post('/leedflow/voip-calls', body, auth.token);
        return (response?.calls ?? []) as VoipCallRow[];
    } catch (error) {
        console.error(error);
        return [];
    }
}

export default listVoipCalls;
